import React from 'react'

import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import Stack from '@mui/material/Stack'

import { ChevronRight } from '@mxenabled/mx-icons'

import { H3, P, Text } from '@mxenabled/mxui'

import { Drawer, useGlobalCopyStore, useGoalStore } from '../../common'
import { Goal, TrackType } from '../../core'

import { ContributionAction } from './actions/ContributionAction'
import { OngoingListItem } from './managegoals/OngoingListItem'
import { CompleteListItem } from './managegoals/CompleteListItem'

interface ManageGoalProps {
  trackType: TrackType
}

export const ManageGoals = ({ trackType }: ManageGoalProps) => {
  const { goals: copy, common: commonCopy } = useGlobalCopyStore()
  const { loadGoals, orderedGoals: allGoals, reorderGoals } = useGoalStore()

  const goals = allGoals.filter((goal) => goal.track_type === trackType)
  const ongoing = goals.filter(
    (goal) =>
      (goal.track_type === TrackType.SAVINGS_TRACK && !goal.has_been_spent) ||
      (goal.track_type !== TrackType.SAVINGS_TRACK && !goal.is_complete),
  )
  const complete = goals.filter(
    (goal) =>
      (goal.track_type === TrackType.SAVINGS_TRACK && goal.has_been_spent) ||
      (goal.track_type !== TrackType.SAVINGS_TRACK && goal.is_complete),
  )

  const [isOpen, setIsOpen] = React.useState(false)

  let manageButtonLabel = copy.manage_saving_goals
  let drawerTitle = copy.manage_saving_drawer_title
  let manageTitle = copy.manage_saving_title
  let manageDescription = copy.manage_saving_description

  if (trackType === TrackType.DEBT_TRACK) {
    manageButtonLabel = copy.manage_debt_goals
    drawerTitle = copy.manage_debt_drawer_title
    manageTitle = copy.manage_debt_title
    manageDescription = copy.manage_debt_description
  } else if (trackType === TrackType.RETIREMENT_TRACK) {
    manageButtonLabel = copy.manage_retirement_goals
    drawerTitle = copy.manage_retirement_drawer_title
    manageTitle = copy.manage_retirement_title
    manageDescription = copy.manage_retirement_description
  }

  const handleMoveGoalUp = async (goal: Goal) => {
    const reordered = [...ongoing]
    const index = reordered.findIndex((g) => g.guid === goal.guid)

    let newIndex = index - 1
    if (newIndex < 0) {
      newIndex = reordered.length - 1
    }

    reordered.splice(newIndex, 0, reordered.splice(index, 1)[0])

    for (let i = 0; i < reordered.length; i++) {
      reordered[i].position = i + 1
    }

    await reorderGoals(reordered)
    await loadGoals()
  }

  return (
    <Stack>
      <Button endIcon={<ChevronRight />} onClick={() => setIsOpen(true)}>
        {manageButtonLabel}
      </Button>

      <Drawer
        ariaLabelClose={commonCopy.close_aria}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={drawerTitle}
      >
        <Stack>
          <Stack m={24}>
            <H3>{manageTitle}</H3>
            <P color="text.secondary" variant="subtitle2">
              {manageDescription}
            </P>
          </Stack>

          <ContributionAction trackType={trackType} />

          <Text bold={true} sx={{ mb: 4, mx: 24, mt: 12 }} variant="caption">
            {copy.goal_priority}
          </Text>

          <List sx={{ bgcolor: 'background.paper' }}>
            {ongoing.map((goal, index) => {
              const isLast = index >= ongoing.length - 1
              return (
                <Stack key={`ongoing-${goal.guid}`}>
                  <OngoingListItem goal={goal} index={index} onUpClick={handleMoveGoalUp} />
                  <Divider variant={isLast ? 'fullWidth' : 'inset'} />
                </Stack>
              )
            })}
          </List>

          {trackType !== TrackType.RETIREMENT_TRACK && complete.length > 0 && (
            <Stack>
              <Text bold={true} sx={{ mb: 4, mx: 24, mt: 12 }} variant="caption">
                {copy.complete_title}
              </Text>

              <List sx={{ bgcolor: 'background.paper' }}>
                {complete.map((goal, index) => {
                  const isLast = index > ongoing.length - 1
                  return (
                    <Stack key={`complete-${goal.guid}`}>
                      <CompleteListItem goal={goal} />
                      <Divider variant={isLast ? 'fullWidth' : 'inset'} />
                    </Stack>
                  )
                })}
              </List>
            </Stack>
          )}
        </Stack>
      </Drawer>
    </Stack>
  )
}
